import React from "react";
import {
  Dumbbell,
  Bike,
  Footprints,
  Trophy,
  Target,
  Mountain,
  Waves,
  Shield,
  Timer,
  HeartPulse,
  Shirt,
  Backpack,
  Tent,
  Snowflake,
  Flame,
  Compass,
  Gamepad2,
} from "lucide-react";

/* ================= TYPES ================= */
export interface CategoryItem {
  id: number;
  name: string;
  icon: React.ComponentType<{ size?: number; color?: string }>;
  parentId?: number | null;
  color?: string;
     image?: any;
}

/* ──────────────────────────────────────────────
   1️⃣ TOP-LEVEL SPORTS & OUTDOORS CATEGORIES (800000xx)
   ────────────────────────────────────────────── */
export const sportsCategories: CategoryItem[] = [
  { id: 80000001, name: "Sports & Outdoors", icon: Trophy, color: "#F57C00" },
  { id: 80000002, name: "Exercise & Fitness", icon: Dumbbell, color: "#D32F2F" },
  { id: 80000003, name: "Team Sports", icon: Target, color: "#1976D2" },
  { id: 80000004, name: "Outdoor Recreation", icon: Mountain, color: "#388E3C" },
  { id: 80000005, name: "Cycling", icon: Bike, color: "#0288D1" },
  { id: 80000006, name: "Running & Walking", icon: Footprints, color: "#5D4037" },
  { id: 80000007, name: "Water Sports", icon: Waves, color: "#0097A7" },
  { id: 80000008, name: "Winter Sports", icon: Snowflake, color: "#1565C0" },
  { id: 80000009, name: "Hunting & Fishing", icon: Compass, color: "#6D4C41" },
  { id: 80000010, name: "Fan Shop & Memorabilia", icon: Shirt, color: "#8E24AA" },
  { id: 80000011, name: "Sports Apparel", icon: Shirt, color: "#C2185B" },
  { id: 80000012, name: "Sports Accessories", icon: Backpack, color: "#455A64" },
  { id: 80000013, name: "Camping & Hiking", icon: Tent, color: "#2E7D32" },
  { id: 80000014, name: "Boxing & Martial Arts", icon: Shield, color: "#B71C1C" },
  { id: 80000015, name: "Sports Electronics", icon: HeartPulse, color: "#AD1457" },
  { id: 80000016, name: "Games & Recreation", icon: Gamepad2, color: "#512DA8" },
];

/* ──────────────────────────────────────────────
   2️⃣ SPORTS SUBCATEGORIES (80000xxx)
   ────────────────────────────────────────────── */
export const sportsSubcategories: CategoryItem[] = [
  // Exercise & Fitness
  { id: 80000201, parentId: 80000002, name: "Strength Training", icon: Dumbbell },
  { id: 80000202, parentId: 80000002, name: "Cardio Equipment", icon: HeartPulse },
  { id: 80000203, parentId: 80000002, name: "Yoga & Pilates", icon: Flame },
  { id: 80000204, parentId: 80000002, name: "Home Gyms", icon: Dumbbell },

  // Team Sports
  { id: 80000301, parentId: 80000003, name: "Football (Soccer)", icon: Target },
  { id: 80000302, parentId: 80000003, name: "Basketball", icon: Target },
  { id: 80000303, parentId: 80000003, name: "Cricket", icon: Target },
  { id: 80000304, parentId: 80000003, name: "Rugby", icon: Target },
  { id: 80000305, parentId: 80000003, name: "Volleyball", icon: Target },

  // Outdoor Recreation
  { id: 80000401, parentId: 80000004, name: "Climbing", icon: Mountain },
  { id: 80000402, parentId: 80000004, name: "Skateboarding", icon: Mountain },
  { id: 80000403, parentId: 80000004, name: "Scooters & Ride-ons", icon: Mountain },

  // Cycling
  { id: 80000501, parentId: 80000005, name: "Bicycles", icon: Bike },
  { id: 80000502, parentId: 80000005, name: "Bike Accessories", icon: Bike },
  { id: 80000503, parentId: 80000005, name: "Bike Maintenance", icon: Bike },

  // Running & Walking
  { id: 80000601, parentId: 80000006, name: "Running Shoes", icon: Footprints },
  { id: 80000602, parentId: 80000006, name: "Fitness Trackers", icon: Timer },
  { id: 80000603, parentId: 80000006, name: "Hydration Gear", icon: Backpack },

  // Water Sports
  { id: 80000701, parentId: 80000007, name: "Swimming", icon: Waves },
  { id: 80000702, parentId: 80000007, name: "Surfing", icon: Waves },
  { id: 80000703, parentId: 80000007, name: "Kayaking & Canoeing", icon: Waves },

  // Winter Sports
  { id: 80000801, parentId: 80000008, name: "Skiing", icon: Snowflake },
  { id: 80000802, parentId: 80000008, name: "Snowboarding", icon: Snowflake },
  { id: 80000803, parentId: 80000008, name: "Ice Skating", icon: Snowflake },

  // Hunting & Fishing
  { id: 80000901, parentId: 80000009, name: "Fishing Gear", icon: Compass },
  { id: 80000902, parentId: 80000009, name: "Hunting Equipment", icon: Compass },

  // Fan Shop
  { id: 80001001, parentId: 80000010, name: "Team Jerseys", icon: Shirt },
  { id: 80001002, parentId: 80000010, name: "Collectibles", icon: Trophy },

  // Apparel
  { id: 80001101, parentId: 80000011, name: "Men’s Sportswear", icon: Shirt },
  { id: 80001102, parentId: 80000011, name: "Women’s Sportswear", icon: Shirt },
  { id: 80001103, parentId: 80000011, name: "Kids’ Sportswear", icon: Shirt },

  // Accessories
  { id: 80001201, parentId: 80000012, name: "Sports Bags", icon: Backpack },
  { id: 80001202, parentId: 80000012, name: "Protective Gear", icon: Shield },

  // Camping
  { id: 80001301, parentId: 80000013, name: "Tents & Shelters", icon: Tent },
  { id: 80001302, parentId: 80000013, name: "Backpacks & Sleeping Bags", icon: Backpack },

  // Boxing & Martial Arts
  { id: 80001401, parentId: 80000014, name: "Boxing Gloves", icon: Shield },
  { id: 80001402, parentId: 80000014, name: "Martial Arts Gear", icon: Shield },

  // Sports Electronics
  { id: 80001501, parentId: 80000015, name: "Smartwatches", icon: HeartPulse },
  { id: 80001502, parentId: 80000015, name: "Heart Rate Monitors", icon: HeartPulse },

  // Games & Recreation
  { id: 80001601, parentId: 80000016, name: "Table Games", icon: Gamepad2 },
  { id: 80001602, parentId: 80000016, name: "Outdoor Games", icon: Gamepad2 },
];

/* ──────────────────────────────────────────────
   3️⃣ SPORTS PRODUCTS (80010101+)
   ────────────────────────────────────────────── */
export const sportsProducts: CategoryItem[] = [
  // Strength Training
  { id: 80020101, parentId: 80000201, name: "Adjustable Dumbbells", icon: Dumbbell },
  { id: 80020102, parentId: 80000201, name: "Kettlebell Set", icon: Dumbbell },

  // Cardio
  { id: 80020201, parentId: 80000202, name: "Treadmill", icon: HeartPulse },
  { id: 80020202, parentId: 80000202, name: "Exercise Bike", icon: Bike },

  // Cycling
  { id: 80050101, parentId: 80000501, name: "Mountain Bike", icon: Bike },
  { id: 80050102, parentId: 80000501, name: "Road Bike", icon: Bike },

  // Running
  { id: 80060101, parentId: 80000601, name: "Running Shoes", icon: Footprints },
  { id: 80060102, parentId: 80000601, name: "Smart Fitness Watch", icon: Timer },

  // Camping
  { id: 800130101, parentId: 80001301, name: "4-Person Tent", icon: Tent },
  { id: 800130102, parentId: 80001301, name: "Sleeping Bag", icon: Backpack },

  // Team Sports
  { id: 80030101, parentId: 80000301, name: "Football", icon: Target },
  { id: 80030201, parentId: 80000302, name: "Basketball", icon: Target },

  // Water Sports
  { id: 80070101, parentId: 80000701, name: "Swimming Goggles", icon: Waves },
  { id: 80070301, parentId: 80000703, name: "Kayak", icon: Waves },
];
